import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import app, { storage } from "../firebase";

const CLOUD_NAME = "dwehroyqh";
const UPLOAD_PRESET = "unsigned_preset";
const FALLBACK_STORAGE_BUCKETS = [
  storage,
  getStorage(app, "gs://unlimited-session.appspot.com"),
  getStorage(app, "gs://unlimited-session.firebasestorage.app"),
];

function errorLabel(error) {
  return error?.code || error?.message || String(error || "erreur inconnue");
}

function cleanName(name) {
  return String(name || "media")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

function isVideoFile(file) {
  return String(file?.type || "").startsWith("video/");
}

async function uploadToCloudinary(file, folder, onProgress) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", folder);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`);
    request.timeout = isVideoFile(file) ? 300000 : 60000;

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.min(95, Math.round((event.loaded / event.total) * 95)));
    };
    request.onerror = () => reject(new Error("Connexion à Cloudinary impossible."));
    request.ontimeout = () => reject(new Error("L'upload Cloudinary a dépassé le délai autorisé."));
    request.onload = () => {
      let payload = {};
      try {
        payload = JSON.parse(request.responseText || "{}");
      } catch {
        reject(new Error("Réponse Cloudinary invalide."));
        return;
      }

      if (request.status < 200 || request.status >= 300 || !payload.secure_url) {
        reject(new Error(payload?.error?.message || `Cloudinary HTTP ${request.status}`));
        return;
      }

      if (onProgress) onProgress(100);
      resolve(payload.secure_url);
    };
    request.send(formData);
  });
}

async function uploadToFirebaseBucket(bucket, file, folder, onProgress) {
  const path = `admin-uploads/${folder}/${Date.now()}-${cleanName(file.name)}`;
  const fileRef = ref(bucket, path);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, {
      contentType: file.type || "application/octet-stream",
    });

    task.on("state_changed", (snapshot) => {
      if (!snapshot.totalBytes || !onProgress) return;
      onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
    }, reject, async () => {
      try {
        resolve(await getDownloadURL(task.snapshot.ref));
      } catch (err) {
        reject(err);
      }
    });
  });
}

async function uploadToFirebase(file, folder, onProgress) {
  const errors = [];

  for (const bucket of FALLBACK_STORAGE_BUCKETS) {
    try {
      return await uploadToFirebaseBucket(bucket, file, folder, onProgress);
    } catch (err) {
      errors.push(errorLabel(err));
    }
  }

  throw new Error(`Firebase Storage: ${errors.join(" | ")}`);
}

export async function uploadAdminMedia(file, folder, options = {}) {
  if (!file) throw new Error("Aucun fichier sélectionné.");
  const { onProgress } = options;

  if (isVideoFile(file)) {
    let cloudinaryError = "";
    try {
      return await uploadToCloudinary(file, folder, onProgress);
    } catch (err) {
      cloudinaryError = errorLabel(err);
    }

    try {
      return await uploadToFirebase(file, folder, onProgress);
    } catch (err) {
      throw new Error(`Cloudinary: ${cloudinaryError}. ${errorLabel(err)}`);
    }
  }

  let cloudinaryError = "";
  try {
    return await uploadToCloudinary(file, folder, onProgress);
  } catch (err) {
    cloudinaryError = errorLabel(err);
  }

  try {
    return await uploadToFirebase(file, folder, onProgress);
  } catch (err) {
    throw new Error(`Cloudinary: ${cloudinaryError}. ${errorLabel(err)}`);
  }
}

export async function deleteAdminMedia(url) {
  if (!url || !String(url).includes("firebasestorage.googleapis.com")) return;

  try {
    await deleteObject(ref(storage, url));
  } catch (err) {
    console.warn("Suppression fichier Firebase Storage impossible", err);
  }
}
